import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Modal,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import {
  findExactMeridian,
  findSimilarMeridians,
  suggestedDimMakMeridianImageUrl,
} from '../utils/dimMakMeridians';

export default function DimMakMeridiansBuilderPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [builderTab, setBuilderTab] = useState('new');
  const [meridians, setMeridians] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showMeridianDropdown, setShowMeridianDropdown] = useState(false);
  const [meridianMenuRect, setMeridianMenuRect] = useState(null);
  const meridianTriggerRef = useRef(null);

  const [meridianName, setMeridianName] = useState('');
  const [meridianDescription, setMeridianDescription] = useState('');
  const [totalPoints, setTotalPoints] = useState('');
  const [mainImage, setMainImage] = useState('');
  const [imageTouched, setImageTouched] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [similarPrompt, setSimilarPrompt] = useState(null);

  const styles = useMemo(() => getStyles(theme), [theme]);

  const fetchUser = useCallback(async (userId) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/users/${userId}`);
      if (response.ok) setCurrentUser(await response.json());
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  }, []);

  const fetchMeridians = useCallback(async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/dim-mak-meridians`);
      if (response.ok) {
        const data = await response.json();
        setMeridians(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching Dim Mak meridians:', error);
    }
  }, []);

  useEffect(() => {
    const userId = route?.params?.userId || route?.params?.user?.id || 1;
    fetchUser(userId);
    fetchMeridians();
  }, [route, fetchUser, fetchMeridians]);

  const resetForm = useCallback(() => {
    setMeridianName('');
    setMeridianDescription('');
    setTotalPoints('');
    setMainImage('');
    setImageTouched(false);
    setSelectedId(null);
    setSaveStatus(null);
    setErrorMessage('');
    setSimilarPrompt(null);
    setShowMeridianDropdown(false);
    setMeridianMenuRect(null);
  }, []);

  const populateForm = useCallback((row) => {
    setSelectedId(row.id);
    setMeridianName(row.meridian_name || '');
    setMeridianDescription(row.meridian_description || '');
    setTotalPoints(row.total_dim_mak_points != null ? String(row.total_dim_mak_points) : '');
    setMainImage(row.main_image || '');
    setImageTouched(Boolean(row.main_image));
    setSaveStatus(null);
    setErrorMessage('');
    setShowMeridianDropdown(false);
    setMeridianMenuRect(null);
  }, []);

  const switchTab = (tab) => {
    if (tab === builderTab) return;
    setBuilderTab(tab);
    resetForm();
  };

  const liveSimilar = useMemo(() => {
    if (!meridianName.trim()) return [];
    return findSimilarMeridians(meridians, meridianName, builderTab === 'edit' ? selectedId : null);
  }, [meridians, meridianName, builderTab, selectedId]);

  const handleNameChange = (value) => {
    setMeridianName(value);
    if (!imageTouched) {
      setMainImage(suggestedDimMakMeridianImageUrl(value));
    }
  };

  const saveMeridian = async (confirmSimilar = false) => {
    const name = meridianName.trim();
    if (!name) {
      setSaveStatus('error');
      setErrorMessage('Meridian Name is required.');
      return;
    }
    if (totalPoints.trim() && (!/^\d+$/.test(totalPoints.trim()) || Number(totalPoints) < 1)) {
      setSaveStatus('error');
      setErrorMessage('Total Dim Mak points must be a positive number.');
      return;
    }

    const excludeId = builderTab === 'edit' ? selectedId : null;
    const exact = findExactMeridian(meridians, name, excludeId);
    if (exact) {
      setSaveStatus('error');
      setErrorMessage(
        `A Dim Mak meridian named "${exact.meridian_name}" already exists (id ${exact.id}). Edit that record instead.`
      );
      return;
    }

    const similar = findSimilarMeridians(meridians, name, excludeId);
    if (similar.length > 0 && !confirmSimilar) {
      setSimilarPrompt({
        message: `A similar Dim Mak meridian already exists: “${similar[0].meridian_name}”. “${name}” looks like the same meridian.`,
        similar,
      });
      return;
    }

    if (builderTab === 'edit' && !selectedId) {
      setSaveStatus('error');
      setErrorMessage('Select a meridian to edit.');
      return;
    }

    if (saving) return;
    setSaving(true);
    setSaveStatus(null);
    setErrorMessage('');
    setSimilarPrompt(null);

    try {
      const payload = {
        meridian_name: name,
        meridian_description: meridianDescription,
        total_dim_mak_points: totalPoints.trim() ? Number(totalPoints.trim()) : null,
        main_image: mainImage.trim() || null,
        confirm_similar: confirmSimilar,
      };
      const url =
        builderTab === 'edit'
          ? `${getApiUrl()}/api/dim-mak-meridians/${selectedId}`
          : `${getApiUrl()}/api/dim-mak-meridians`;
      const response = await fetch(url, {
        method: builderTab === 'edit' ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (data.requires_confirmation) {
          setSimilarPrompt({
            message: data.error || 'A similar meridian already exists.',
            similar: data.similar || [],
          });
          return;
        }
        setSaveStatus('error');
        setErrorMessage(data.error || 'Failed to save Dim Mak meridian.');
        return;
      }
      setSaveStatus('success');
      await fetchMeridians();
      if (builderTab === 'edit' && data?.id) {
        populateForm(data);
        setSaveStatus('success');
      }
    } catch (error) {
      console.error('Error saving Dim Mak meridian:', error);
      setSaveStatus('error');
      setErrorMessage('Failed to save Dim Mak meridian. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const selectedMeridian = meridians.find((m) => m.id === selectedId) || null;

  const closeMeridianDropdown = useCallback(() => {
    setShowMeridianDropdown(false);
    setMeridianMenuRect(null);
  }, []);

  const toggleMeridianDropdown = useCallback(() => {
    if (showMeridianDropdown) {
      closeMeridianDropdown();
      return;
    }
    if (Platform.OS === 'web' && meridianTriggerRef.current?.measureInWindow) {
      meridianTriggerRef.current.measureInWindow((x, y, width, height) => {
        const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
        const top = y + height + 4;
        const maxHeight = Math.max(120, Math.min(280, viewportH - top - 16));
        setMeridianMenuRect({
          top,
          left: x,
          width: Math.max(width, 200),
          maxHeight,
        });
        setShowMeridianDropdown(true);
      });
      return;
    }
    setShowMeridianDropdown(true);
  }, [showMeridianDropdown, closeMeridianDropdown]);

  const meridianMenuReady = Platform.OS !== 'web' || meridianMenuRect != null;

  const meridianMenuList = (
    <ScrollView
      style={[
        styles.dropdownScroll,
        Platform.OS === 'web' && meridianMenuRect
          ? { maxHeight: meridianMenuRect.maxHeight }
          : null,
      ]}
      nestedScrollEnabled
      keyboardShouldPersistTaps="handled"
      {...(Platform.OS === 'web' ? { dataSet: { dimMakMeridianDropdownScroll: 'true' } } : {})}
    >
      {meridians.map((row) => (
        <TouchableOpacity
          key={row.id}
          style={styles.dropdownRow}
          onPress={() => populateForm(row)}
          {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
        >
          <Text style={styles.dropdownRowText}>{row.meridian_name}</Text>
        </TouchableOpacity>
      ))}
      {meridians.length === 0 ? (
        <Text style={styles.dropdownEmpty}>No meridians created yet.</Text>
      ) : null}
    </ScrollView>
  );

  const meridianMenuNode =
    showMeridianDropdown && meridianMenuReady ? (
      <>
        {Platform.OS === 'web' ? (
          <TouchableOpacity
            style={styles.dropdownOverlay}
            activeOpacity={1}
            onPress={closeMeridianDropdown}
            {...(Platform.OS === 'web' ? { dataSet: { dimMakMeridianDropdownOverlay: 'true' } } : {})}
          />
        ) : null}
        <View
          style={[
            styles.dropdownList,
            Platform.OS === 'web' && meridianMenuRect
              ? {
                  position: 'fixed',
                  top: meridianMenuRect.top,
                  left: meridianMenuRect.left,
                  width: meridianMenuRect.width,
                  right: undefined,
                  marginTop: 0,
                  maxHeight: meridianMenuRect.maxHeight,
                }
              : null,
          ]}
          {...(Platform.OS === 'web' ? { dataSet: { dimMakMeridianDropdownMenu: 'true' } } : {})}
        >
          {meridianMenuList}
        </View>
      </>
    ) : null;

  const portaledMeridianMenu =
    Platform.OS === 'web' && meridianMenuNode && typeof document !== 'undefined'
      ? require('react-dom').createPortal(
          <div
            data-dim-mak-meridian-dropdown-portal="true"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 100000,
              pointerEvents: 'none',
            }}
          >
            <div style={{ pointerEvents: 'auto', width: '100%', height: '100%' }}>
              {meridianMenuNode}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <View
      style={styles.container}
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'dim-mak-meridians-builder' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Dim mak Meridians</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.titleSection}>
          <Text style={styles.title}>Dim mak Meridians</Text>
          <Text style={styles.subtitle}>
            Create or edit a Dim Mak meridian. The handbook image comes from the dim_mak_meridians
            bucket and should be named the same as the meridian (for example Lung).
          </Text>
        </View>

        <View style={styles.pageTabRow}>
          <TouchableOpacity
            style={[styles.pageTab, builderTab === 'new' && styles.pageTabOn]}
            onPress={() => switchTab('new')}
            disabled={saving}
          >
            <Text style={[styles.pageTabText, builderTab === 'new' && styles.pageTabTextOn]}>New</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.pageTab, builderTab === 'edit' && styles.pageTabOn]}
            onPress={() => switchTab('edit')}
            disabled={saving}
          >
            <Text style={[styles.pageTabText, builderTab === 'edit' && styles.pageTabTextOn]}>Edit</Text>
          </TouchableOpacity>
        </View>

        {saveStatus && (
          <View
            style={[
              styles.statusBox,
              saveStatus === 'success' ? styles.successBox : styles.errorBox,
            ]}
          >
            <Text
              style={[
                styles.statusBoxTitle,
                saveStatus === 'success' ? styles.successBoxText : styles.errorBoxText,
              ]}
            >
              {saveStatus === 'success' ? 'Record saved successfully!' : 'Error'}
            </Text>
            {saveStatus === 'error' && errorMessage ? (
              <Text style={styles.errorMessage}>{errorMessage}</Text>
            ) : null}
            {saveStatus === 'success' ? (
              <View style={styles.statusButtons}>
                <TouchableOpacity
                  style={[styles.statusButton, styles.backButton]}
                  onPress={() =>
                    navigation.navigate('DimMakBuilder', {
                      userId: currentUser?.id || 1,
                      user: currentUser,
                    })
                  }
                >
                  <Text style={styles.statusButtonText}>Back to Dim mak builder</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.statusButton, styles.resetStatusButton]}
                  onPress={resetForm}
                >
                  <Text style={styles.statusButtonText}>Reset form</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity
                style={[styles.statusButton, styles.resetStatusButton]}
                onPress={() => {
                  setSaveStatus(null);
                  setErrorMessage('');
                }}
              >
                <Text style={styles.statusButtonText}>Try Again</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {!saveStatus && (
          <View style={styles.form}>
            {builderTab === 'edit' && (
              <View style={styles.fieldContainer}>
                <Text style={styles.label}>Select meridian</Text>
                <View ref={meridianTriggerRef} collapsable={false}>
                  <TouchableOpacity
                    style={styles.dropdownTrigger}
                    onPress={toggleMeridianDropdown}
                    {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                  >
                    <Text style={styles.dropdownTriggerText}>
                      {selectedMeridian?.meridian_name || 'Select a meridian'}
                    </Text>
                    <Text style={styles.dropdownChevron}>{showMeridianDropdown ? '▲' : '▼'}</Text>
                  </TouchableOpacity>
                </View>
                {Platform.OS !== 'web' ? meridianMenuNode : null}
              </View>
            )}

            {builderTab === 'new' && liveSimilar.length > 0 ? (
              <View style={styles.warningBox}>
                <Text style={styles.warningText}>
                  Similar meridian already created: {liveSimilar.map((r) => r.meridian_name).join(', ')}.
                  Saving “{meridianName.trim()}” will flag this match.
                </Text>
              </View>
            ) : null}

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Meridian Name *</Text>
              <TextInput
                style={styles.input}
                value={meridianName}
                onChangeText={handleNameChange}
                placeholder="e.g. Lung"
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              />
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Meridian Description</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={meridianDescription}
                onChangeText={setMeridianDescription}
                placeholder="Long description of this meridian"
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                multiline
                numberOfLines={8}
                textAlignVertical="top"
              />
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Total Dim Mak points</Text>
              <TextInput
                style={styles.input}
                value={totalPoints}
                onChangeText={setTotalPoints}
                placeholder="e.g. 11"
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                keyboardType="number-pad"
              />
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Main image URL</Text>
              <Text style={styles.helpText}>
                Stored as a URL. Images live in the dim_mak_meridians bucket and are named the same as
                the meridian.
              </Text>
              <TextInput
                style={styles.input}
                value={mainImage}
                onChangeText={(v) => {
                  setImageTouched(true);
                  setMainImage(v);
                }}
                placeholder="https://…/dim_mak_meridians/Lung"
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                autoCapitalize="none"
              />
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity style={[styles.button, styles.resetButton]} onPress={resetForm}>
                <Text style={styles.buttonText}>Reset</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.button, styles.saveButton, saving && styles.saveButtonDisabled]}
                onPress={() => saveMeridian(false)}
                disabled={saving}
              >
                <Text style={styles.buttonText}>{saving ? 'Saving…' : 'Save'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </ScrollView>

      <Modal visible={!!similarPrompt} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View
            style={styles.modalCard}
            {...(Platform.OS === 'web' ? { dataSet: { dimMakSimilarModal: 'true' } } : {})}
          >
            <Text style={styles.modalTitle}>Similar meridian</Text>
            <Text style={styles.modalBody}>{similarPrompt?.message}</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalCancel]}
                onPress={() => setSimilarPrompt(null)}
              >
                <Text style={styles.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalConfirm]}
                onPress={() => saveMeridian(true)}
              >
                <Text style={styles.modalConfirmText}>Save anyway</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      {portaledMeridianMenu}
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: 'transparent',
    },
    hamburgerContainer: { marginRight: 15, backgroundColor: 'transparent' },
    breadcrumbContainer: { flex: 1 },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
      backgroundColor: 'transparent',
    },
    profileIconContainer: { backgroundColor: 'transparent' },
    content: { flex: 1 },
    contentContainer: { padding: 20, paddingBottom: 40 },
    titleSection: { marginBottom: 24, alignItems: 'center' },
    title: {
      fontSize: 32,
      fontWeight: 'bold',
      color: theme.isDarkMode ? '#FDFD0A' : '#000000',
      marginBottom: 10,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    subtitle: {
      fontSize: 14,
      fontStyle: 'italic',
      color: theme.colors.text,
      textAlign: 'center',
      lineHeight: 20,
      opacity: 0.8,
      backgroundColor: 'transparent',
    },
    pageTabRow: {
      flexDirection: 'row',
      gap: 8,
      marginBottom: 24,
      maxWidth: 600,
      alignSelf: 'center',
      width: '100%',
    },
    pageTab: {
      flex: 1,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
      backgroundColor: theme.isDarkMode ? '#3a3a3a' : '#E8E8E8',
    },
    pageTabOn: { backgroundColor: theme.colors.primary || '#d4af37' },
    pageTabText: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    pageTabTextOn: { color: '#000', backgroundColor: 'transparent' },
    form: { width: '100%', maxWidth: 600, alignSelf: 'center' },
    fieldContainer: { marginBottom: 20 },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    helpText: {
      fontSize: 12,
      color: theme.colors.text,
      opacity: 0.7,
      marginBottom: 8,
      fontStyle: 'italic',
      backgroundColor: 'transparent',
    },
    input: {
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderRadius: 8,
      padding: 12,
      fontSize: 16,
      color: theme.colors.text,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    textArea: { minHeight: 160, textAlignVertical: 'top' },
    dropdownTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderRadius: 8,
      padding: 12,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    dropdownTriggerText: {
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
      flex: 1,
    },
    dropdownChevron: {
      fontSize: 14,
      color: theme.colors.text,
      backgroundColor: 'transparent',
      marginLeft: 8,
    },
    dropdownList: {
      marginTop: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      maxHeight: 280,
      overflow: 'hidden',
      zIndex: 100002,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 4px 16px rgba(0, 0, 0, 0.28)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.25,
            shadowRadius: 8,
            elevation: 12,
          }),
    },
    dropdownScroll: {
      maxHeight: 280,
    },
    dropdownOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'transparent',
      zIndex: 100001,
    },
    dropdownRow: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    dropdownRowText: {
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    dropdownEmpty: {
      padding: 12,
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.7,
      backgroundColor: 'transparent',
    },
    warningBox: {
      backgroundColor: theme.isDarkMode ? '#4a3a12' : '#FFF8E1',
      borderRadius: 8,
      padding: 12,
      marginBottom: 16,
      borderWidth: 1,
      borderColor: '#d4af37',
    },
    warningText: {
      fontSize: 14,
      color: theme.colors.text,
      backgroundColor: 'transparent',
      lineHeight: 20,
    },
    buttonContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 12,
      gap: 15,
    },
    button: {
      flex: 1,
      padding: 15,
      borderRadius: 8,
      alignItems: 'center',
    },
    resetButton: { backgroundColor: theme.isDarkMode ? '#555' : '#9e9e9e' },
    saveButton: { backgroundColor: theme.colors.primary || '#d4af37' },
    saveButtonDisabled: { opacity: 0.6 },
    buttonText: {
      color: '#000',
      fontSize: 16,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    statusBox: {
      width: '100%',
      maxWidth: 600,
      alignSelf: 'center',
      borderRadius: 8,
      padding: 16,
      marginBottom: 20,
    },
    successBox: { backgroundColor: theme.isDarkMode ? '#1b3d1b' : '#E8F5E9' },
    errorBox: { backgroundColor: theme.isDarkMode ? '#4a2020' : '#FFEBEE' },
    statusBoxTitle: {
      fontSize: 16,
      fontWeight: '700',
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    successBoxText: { color: theme.isDarkMode ? '#81c784' : '#2E7D32' },
    errorBoxText: { color: theme.isDarkMode ? '#ff8a80' : '#C62828' },
    errorMessage: {
      fontSize: 14,
      color: theme.isDarkMode ? '#ff8a80' : '#C62828',
      backgroundColor: 'transparent',
      marginBottom: 8,
    },
    statusButtons: { flexDirection: 'row', gap: 10, marginTop: 8 },
    statusButton: {
      flex: 1,
      padding: 12,
      borderRadius: 8,
      alignItems: 'center',
    },
    backButton: { backgroundColor: theme.colors.primary || '#d4af37' },
    resetStatusButton: { backgroundColor: theme.isDarkMode ? '#555' : '#9e9e9e' },
    statusButtonText: {
      color: '#000',
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.55)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24,
    },
    modalCard: {
      width: '100%',
      maxWidth: 420,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
      borderRadius: 12,
      padding: 20,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    modalTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.heading,
      marginBottom: 10,
      backgroundColor: 'transparent',
    },
    modalBody: {
      fontSize: 15,
      lineHeight: 22,
      color: theme.colors.text,
      marginBottom: 18,
      backgroundColor: 'transparent',
    },
    modalButtons: { flexDirection: 'row', gap: 10 },
    modalButton: {
      flex: 1,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
    },
    modalCancel: { backgroundColor: theme.isDarkMode ? '#555' : '#9e9e9e' },
    modalConfirm: { backgroundColor: theme.colors.primary || '#d4af37' },
    modalCancelText: { color: '#000', fontWeight: '700', backgroundColor: 'transparent' },
    modalConfirmText: { color: '#000', fontWeight: '700', backgroundColor: 'transparent' },
  });
